import type { CmmdBoardTask, EnvironmentId, EnvironmentApi, ThreadId } from "@cmmd-forge/contracts";
import { useQuery } from "@tanstack/react-query";
import { CheckIcon, LinkIcon, Loader2Icon, SearchIcon, TargetIcon } from "lucide-react";
import { useState } from "react";
import { buildCmmdTaskGoal } from "@cmmd-forge/shared/cmmdTaskGoal";

import { readEnvironmentApi } from "~/environmentApi";
import { createCmmdApiClient } from "~/environments/cmmdApi";
import { newCommandId } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "~/components/ui/popover";
import { toastManager } from "~/components/ui/toast";
import { cn } from "~/lib/utils";
import { COMPOSER_CONTROL_CLASS } from "./ComposerControl";
import { COMPOSER_FOCUS_RING_CLASS } from "./composerFocusRing";

const cmmdApi = createCmmdApiClient();

interface ComposerTaskAnchorPickerProps {
  readonly projectId: string;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly currentTaskId: string | null;
  readonly disabled: boolean;
  /**
   * Controlled from a menu item rather than from this picker's own trigger.
   *
   * A menu item unmounts its subtree when the menu closes, and the menu closes
   * on the very click meant to open this picker. So when the caller supplies
   * `open` the trigger is dropped and the popup anchors to whatever element the
   * caller points `anchor` at, which is the menu's trigger.
   */
  readonly open?: boolean | undefined;
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
  readonly anchor?: Element | null | undefined;
}

export function ComposerTaskAnchorPicker(props: ComposerTaskAnchorPickerProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = props.open !== undefined;
  const open = props.open ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (props.onOpenChange) props.onOpenChange(next);
    if (!controlled) setUncontrolledOpen(next);
  };
  const [query, setQuery] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [attaching, setAttaching] = useState(false);
  const tasksQuery = useQuery({
    queryKey: ["composer-cmmd-tasks", props.projectId],
    queryFn: () => cmmdApi.listProjectBoard(props.projectId),
    enabled: open,
    staleTime: 30_000,
  });
  const tasks = tasksQuery.data ?? [];
  const normalizedQuery = query.trim().toLowerCase();
  const visibleTasks = tasks.filter(
    (task) =>
      !task.archived &&
      (normalizedQuery.length === 0 ||
        task.title.toLowerCase().includes(normalizedQuery) ||
        String(task.id).includes(normalizedQuery)),
  );
  const selectedTask = tasks.find((task) => task.id === selectedTaskId) ?? null;

  const handleAttach = async () => {
    if (selectedTask === null) return;
    const api = readEnvironmentApi(props.environmentId);
    if (!api) return;
    setAttaching(true);
    try {
      await attachCmmdTaskToThread(
        api.orchestration.dispatchCommand,
        props.threadId,
        props.projectId,
        selectedTask,
      );
      toastManager.add({
        type: "success",
        title: `Task #${selectedTask.id} attached`,
        description: "Goal updated · Work has not started",
      });
      setOpen(false);
    } catch (cause) {
      toastManager.add({
        type: "error",
        title: "Could not attach task",
        description: cause instanceof Error ? cause.message : "An error occurred.",
      });
    } finally {
      setAttaching(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {controlled ? null : (
        <PopoverTrigger
          disabled={props.disabled}
          aria-label="Attach a CMMD task to this thread"
          // The only trigger this picker has is the Compact footer's, and that
          // footer is the phone's whole control row, so the row's touch
          // minimum applies here as much as it does to the model picker
          // beside it.
          className={cn(
            COMPOSER_CONTROL_CLASS,
            COMPOSER_FOCUS_RING_CLASS,
            "inline-flex items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50 md:text-xs",
          )}
        >
          <LinkIcon className="size-3.5" aria-hidden />
          {props.currentTaskId === null ? "Task" : `Task #${props.currentTaskId}`}
        </PopoverTrigger>
      )}
      <PopoverPopup
        align="start"
        side="top"
        className="w-[min(90vw,24rem)] p-0"
        {...(props.anchor ? { anchor: props.anchor } : {})}
      >
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Attach project task</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Sets this thread’s task anchor and standing goal. It does not start work.
            </p>
          </div>
          <label className="flex items-center gap-2 rounded-md border border-border bg-background px-2">
            <SearchIcon className="size-3.5 text-muted-foreground" aria-hidden />
            <span className="sr-only">Search tasks</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="Search tasks"
              className="h-8 min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </label>
          <div className="max-h-52 overflow-y-auto rounded-md border border-border">
            {tasksQuery.isLoading ? (
              <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
                Loading project tasks…
              </div>
            ) : tasksQuery.isError ? (
              <p className="px-3 py-4 text-sm text-destructive">Could not load project tasks.</p>
            ) : visibleTasks.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">No matching tasks.</p>
            ) : (
              <ol className="divide-y divide-border">
                {visibleTasks.map((task) => {
                  const selected = task.id === selectedTaskId;
                  return (
                    <li key={task.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedTaskId(task.id)}
                        className={cn(
                          "flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-muted",
                          selected ? "bg-muted" : "bg-background",
                        )}
                      >
                        <CheckIcon
                          className={cn(
                            "mt-0.5 size-3.5 shrink-0",
                            selected ? "opacity-100" : "opacity-0",
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground">
                            {task.title}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            Task #{task.id}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
          {selectedTask !== null ? (
            <div className="rounded-md border border-border bg-muted/40 p-2">
              <p className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <TargetIcon className="size-3.5" aria-hidden /> Goal preview
              </p>
              <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">
                {buildCmmdTaskGoal({
                  projectId: props.projectId,
                  taskId: selectedTask.id,
                  title: selectedTask.title,
                  description: selectedTask.description,
                  updatedAt: selectedTask.updatedAt,
                })}
              </p>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={selectedTask === null || attaching}
              onClick={() => void handleAttach()}
            >
              {attaching ? "Attaching…" : "Attach task"}
            </Button>
          </div>
        </div>
      </PopoverPopup>
    </Popover>
  );
}

export async function attachCmmdTaskToThread(
  dispatchCommand: (
    command: Extract<
      Parameters<EnvironmentApi["orchestration"]["dispatchCommand"]>[0],
      { readonly type: "thread.meta.update" }
    >,
  ) => Promise<unknown>,
  threadId: ThreadId,
  expectedProjectId: string,
  task: CmmdBoardTask,
): Promise<void> {
  if (String(task.projectId) !== expectedProjectId) {
    throw new Error("The selected task does not belong to this project.");
  }
  await dispatchCommand({
    type: "thread.meta.update",
    commandId: newCommandId(),
    threadId,
    anchorKind: "task",
    anchorCmmdId: String(task.id),
    goal: buildCmmdTaskGoal({
      projectId: expectedProjectId,
      taskId: task.id,
      title: task.title,
      description: task.description,
      updatedAt: task.updatedAt,
    }),
  });
}
